import React, { useMemo, useState } from 'react';
import { TrendingUp, Search, MapPin, FileText, BarChart3 } from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip
} from 'recharts';
import DataTable from 'components/common/DataTable';
import Pagination from 'components/Pagination';
import MultiSelectFilter from 'components/common/MultiSelectFilter';

const DIMENSION_ORDER = ['cidade', 'assunto', 'subgrupo'];
const BAR_COLOR_PRIMARY = '#475569';
const BAR_COLOR_SECONDARY = '#94A3B8';

const DIMENSION_LABELS = {
  cidade: 'Cidade',
  assunto: 'Assunto',
  subgrupo: 'Subgrupo'
};

const MISSING_LABELS = {
  cidade: 'Sem cidade',
  assunto: 'Sem assunto',
  subgrupo: 'Sem subgrupo'
};

const normalizeLabelText = (value) => String(value || '').replace(/\s+/g, ' ').trim();

const truncateLabel = (value, maxLength = 24) => {
  const normalized = normalizeLabelText(value);
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, Math.max(0, maxLength - 1)).trimEnd()}...`;
};

const normalizeDimensionValue = (item, dimension) => {
  const value = item?.[dimension];
  if (value === null || value === undefined || String(value).trim() === '') {
    return MISSING_LABELS[dimension];
  }
  return normalizeLabelText(value);
};

const getDistinctCounts = (items = []) => {
  const sets = {
    cidade: new Set(),
    assunto: new Set(),
    subgrupo: new Set()
  };

  items.forEach((item) => {
    sets.cidade.add(normalizeDimensionValue(item, 'cidade'));
    sets.assunto.add(normalizeDimensionValue(item, 'assunto'));
    sets.subgrupo.add(normalizeDimensionValue(item, 'subgrupo'));
  });

  return {
    cidade: sets.cidade.size,
    assunto: sets.assunto.size,
    subgrupo: sets.subgrupo.size
  };
};

const getDominantDimension = (counts) => {
  if (counts.cidade > 1) return 'cidade';
  if (counts.assunto > 1) return 'assunto';
  if (counts.subgrupo > 1) return 'subgrupo';
  return 'none';
};

const getNextDimension = (dominant, counts) => {
  if (dominant === 'none') return null;
  const startIndex = DIMENSION_ORDER.indexOf(dominant);
  if (startIndex < 0) return null;

  for (let i = startIndex + 1; i < DIMENSION_ORDER.length; i += 1) {
    const dim = DIMENSION_ORDER[i];
    if (counts[dim] > 1) {
      return dim;
    }
  }

  return null;
};

const normalizeIdsOss = (rawIdsOss) => {
  if (Array.isArray(rawIdsOss)) {
    return [...new Set(rawIdsOss.map((id) => String(id || '').trim()).filter(Boolean))];
  }

  if (typeof rawIdsOss === 'string') {
    return [...new Set(rawIdsOss.split(',').map((id) => id.trim()).filter(Boolean))];
  }

  return [];
};

const getIdsOssForItem = (item = {}) => {
  const idsOss = normalizeIdsOss(item?.ids_oss);
  return idsOss;
};

const getOssCountForItemFallback = (item = {}) => Number(item?.oss_com_uso || 0);

const getUniqueOssCountForItems = (items = []) => {
  const uniqueIds = new Set();
  let fallbackCount = 0;

  items.forEach((item) => {
    const idsOss = getIdsOssForItem(item);
    if (idsOss.length > 0) {
      idsOss.forEach((id) => uniqueIds.add(id));
      return;
    }
    fallbackCount += getOssCountForItemFallback(item);
  });

  return uniqueIds.size + fallbackCount;
};

const buildSeriesByDimension = (items = [], dimension) => {
  if (!dimension || dimension === 'none') return [];

  const grouped = new Map();

  items.forEach((item) => {
    const category = normalizeDimensionValue(item, dimension);
    if (!grouped.has(category)) {
      grouped.set(category, {
        ids: new Set(),
        fallbackCount: 0
      });
    }

    const bucket = grouped.get(category);
    const idsOss = getIdsOssForItem(item);
    if (idsOss.length > 0) {
      idsOss.forEach((id) => bucket.ids.add(id));
      return;
    }

    bucket.fallbackCount += getOssCountForItemFallback(item);
  });

  return Array.from(grouped.entries())
    .map(([name, bucket]) => ({ name, value: bucket.ids.size + bucket.fallbackCount }))
    .sort((a, b) => b.value - a.value);
};

const resolveChartTypeByCategoryCount = (count) => {
  if (count <= 1) return 'none';
  if (count <= 6) return 'vertical';
  if (count <= 15) return 'horizontal';
  return 'top10';
};

const applyTop10PlusOthers = (series = []) => {
  if (!Array.isArray(series) || series.length <= 10) {
    return series;
  }

  const top10 = series.slice(0, 10);
  const othersTotal = series
    .slice(10)
    .reduce((acc, item) => acc + Number(item.value || 0), 0);

  return [...top10, { name: 'Outros', value: othersTotal }];
};

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const item = payload[0]?.payload;
  if (!item) return null;

  return (
    <div className="bg-[color:var(--surface)] p-3 border border-[color:var(--border)] rounded-lg shadow-sm">
      <p className="font-semibold text-[color:var(--text-primary)]">{item.name}</p>
      <p className="text-sm text-[color:var(--text-muted)]">
        OSS com uso: {Number(item.value || 0).toLocaleString('pt-BR')}
      </p>
    </div>
  );
};

const ClickableRowBarShape = (props) => {
  const {
    x,
    y,
    width,
    height,
    fill,
    fillOpacity,
    stroke,
    strokeWidth,
    payload,
    background,
    onSelect
  } = props;

  const barX = Math.min(x, x + width);
  const barWidth = Math.abs(width);
  const rowX = Number(background?.x ?? barX);
  const rowWidth = Number(background?.width ?? barWidth);
  const hitHeight = Math.max(Number(height || 0), 20);
  const hitY = Number(y || 0) - (hitHeight - Number(height || 0)) / 2;

  const handleClick = () => {
    if (typeof onSelect === 'function') {
      onSelect(payload);
    }
  };

  return (
    <g onClick={handleClick} style={{ cursor: 'pointer' }}>
      <rect
        x={rowX}
        y={hitY}
        width={rowWidth}
        height={hitHeight}
        fill="rgba(0,0,0,0.001)"
      />
      <rect
        x={barX}
        y={y}
        width={barWidth}
        height={height}
        fill={fill}
        fillOpacity={fillOpacity}
        stroke={stroke}
        strokeWidth={strokeWidth}
        rx={4}
        ry={4}
      />
    </g>
  );
};

const DynamicBarChartCard = ({
  title,
  subtitle,
  series,
  chartType,
  barColor,
  onBarClick = null,
  activeCategory = null
}) => {
  if (!series || series.length === 0 || chartType === 'none') {
    return (
      <div className="bg-[color:var(--surface-muted)] rounded-lg border border-[color:var(--border)] p-4">
        <h4 className="text-sm font-semibold text-[color:var(--text-primary)] mb-1">{title}</h4>
        <p className="text-xs text-[color:var(--text-muted)] mb-3">{subtitle}</p>
        <div className="h-72 flex items-center justify-center text-sm text-[color:var(--text-muted)]">
          Sem comparação disponível para este eixo
        </div>
      </div>
    );
  }

  const isHorizontal = chartType === 'horizontal' || chartType === 'top10';
  const yAxisLabelWidth = isHorizontal ? 140 : undefined;

  return (
    <div className="bg-[color:var(--surface-muted)] rounded-lg border border-[color:var(--border)] p-4">
      <h4 className="text-sm font-semibold text-[color:var(--text-primary)] mb-1">{title}</h4>
      <p className="text-xs text-[color:var(--text-muted)] mb-3">{subtitle}</p>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={series}
            layout={isHorizontal ? 'vertical' : 'horizontal'}
            margin={{ top: 12, right: 16, left: isHorizontal ? 8 : 8, bottom: isHorizontal ? 8 : 42 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            {isHorizontal ? (
              <>
                <XAxis
                  type="number"
                  tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                  axisLine={{ stroke: 'var(--border)' }}
                  tickLine={{ stroke: 'var(--border)' }}
                />
                <YAxis
                  dataKey="name"
                  type="category"
                  width={yAxisLabelWidth}
                  tickFormatter={(value) => truncateLabel(value, 22)}
                  tick={{ fill: 'var(--text-muted)', fontSize: 10 }}
                  axisLine={{ stroke: 'var(--border)' }}
                  tickLine={{ stroke: 'var(--border)' }}
                />
              </>
            ) : (
              <>
                <XAxis
                  dataKey="name"
                  type="category"
                  interval={0}
                  tickFormatter={(value) => truncateLabel(value, 28)}
                  angle={-18}
                  height={72}
                  tickMargin={8}
                  tick={{ fill: 'var(--text-muted)', fontSize: 11, textAnchor: 'end' }}
                  axisLine={{ stroke: 'var(--border)' }}
                  tickLine={{ stroke: 'var(--border)' }}
                />
                <YAxis
                  type="number"
                  tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                  axisLine={{ stroke: 'var(--border)' }}
                  tickLine={{ stroke: 'var(--border)' }}
                />
              </>
            )}
            <Tooltip content={<ChartTooltip />} />
            <Bar
              dataKey="value"
              fill={barColor}
              radius={[4, 4, 0, 0]}
              cursor={onBarClick ? 'pointer' : 'default'}
              shape={onBarClick ? (shapeProps) => (
                <ClickableRowBarShape
                  {...shapeProps}
                  onSelect={onBarClick}
                />
              ) : undefined}
            >
              {series.map((item) => {
                const isActive = activeCategory && item.name === activeCategory;
                const hasActiveSelection = Boolean(activeCategory);
                return (
                  <Cell
                    key={`cell-${item.name}`}
                    fill={isActive ? 'var(--accent)' : barColor}
                    fillOpacity={hasActiveSelection ? (isActive ? 1 : 0.2) : 1}
                    stroke={isActive ? 'var(--accent)' : 'none'}
                    strokeWidth={isActive ? 2 : 0}
                  />
                );
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const AnaliseProdutosMovimentadosTable = ({ data = [], loading = false }) => {
  const [busca, setBusca] = useState('');
  const [filtroSubgrupo, setFiltroSubgrupo] = useState([]);
  const [filtroCidade, setFiltroCidade] = useState([]);
  const [filtroAssunto, setFiltroAssunto] = useState([]);
  const [cidadeSelecionadaNoGrafico, setCidadeSelecionadaNoGrafico] = useState(null);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const subgrupos = [...new Set(data.map((item) => normalizeDimensionValue(item, 'subgrupo')).filter(Boolean))].sort();
  const cidades = [...new Set(data.map((item) => normalizeDimensionValue(item, 'cidade')).filter(Boolean))].sort();
  const assuntos = [...new Set(data.map((item) => normalizeDimensionValue(item, 'assunto')).filter(Boolean))].sort();

  const dadosBaseFiltrados = useMemo(() => {
    const termoBusca = busca.toLowerCase().trim();

    return (Array.isArray(data) ? data : []).filter((item) => {
      const idProduto = String(item.id_produto || '').toLowerCase();
      const produto = String(item.produto || '').toLowerCase();
      const subgrupo = normalizeDimensionValue(item, 'subgrupo').toLowerCase();
      const cidade = normalizeDimensionValue(item, 'cidade');
      const assunto = normalizeDimensionValue(item, 'assunto');

      const matchBusca = !termoBusca
        || idProduto.includes(termoBusca)
        || produto.includes(termoBusca)
        || subgrupo.includes(termoBusca);

      const matchSubgrupo = filtroSubgrupo.length === 0 || filtroSubgrupo.includes(subgrupo === '' ? MISSING_LABELS.subgrupo : normalizeDimensionValue(item, 'subgrupo'));
      const matchCidade = filtroCidade.length === 0 || filtroCidade.includes(cidade);
      const matchAssunto = filtroAssunto.length === 0 || filtroAssunto.includes(assunto);

      return matchBusca && matchSubgrupo && matchCidade && matchAssunto;
    });
  }, [data, busca, filtroSubgrupo, filtroCidade, filtroAssunto]);

  const dadosAgregadosTabela = useMemo(() => {
    const agrupados = new Map();
    const cidadeConsolidada = filtroCidade.length === 0 ? 'Todas as cidades' : null;

    dadosBaseFiltrados.forEach((item) => {
      const cidade = cidadeConsolidada || normalizeDimensionValue(item, 'cidade');
      const subgrupo = normalizeDimensionValue(item, 'subgrupo');
      const key = [
        item.id_produto || '',
        item.produto || '',
        subgrupo,
        cidade
      ].join('|');

      if (!agrupados.has(key)) {
        agrupados.set(key, {
          id_produto: item.id_produto || '',
          produto: item.produto || '',
          subgrupo,
          cidade,
          assunto: 'Todos os assuntos',
          oss_com_uso: 0,
          qtd_utilizada: 0,
          valor_unitario_revenda: 0,
          valor_total_revenda: 0
        });
      }

      const row = agrupados.get(key);
      const qtdAtual = Number(row.qtd_utilizada || 0);
      const qtdNovo = Number(item.qtd_utilizada || 0);
      const qtdTotal = qtdAtual + qtdNovo;
      const valorUnitAtual = Number(row.valor_unitario_revenda || 0);
      const valorUnitNovo = Number(item.valor_unitario_revenda || 0);

      row.oss_com_uso += Number(item.oss_com_uso || 0);
      row.qtd_utilizada += qtdNovo;
      row.valor_total_revenda += Number(item.valor_total_revenda || 0);

      if (qtdTotal > 0) {
        row.valor_unitario_revenda = ((valorUnitAtual * qtdAtual) + (valorUnitNovo * qtdNovo)) / qtdTotal;
      }
    });

    return Array.from(agrupados.values());
  }, [dadosBaseFiltrados, filtroCidade.length]);

  const counts = useMemo(() => getDistinctCounts(dadosBaseFiltrados), [dadosBaseFiltrados]);
  const dominantDimension = useMemo(() => getDominantDimension(counts), [counts]);
  const secondaryDimension = useMemo(
    () => getNextDimension(dominantDimension, counts),
    [dominantDimension, counts]
  );

  const principalSeriesRaw = useMemo(
    () => buildSeriesByDimension(dadosBaseFiltrados, dominantDimension),
    [dadosBaseFiltrados, dominantDimension]
  );
  const principalChartType = useMemo(
    () => resolveChartTypeByCategoryCount(principalSeriesRaw.length),
    [principalSeriesRaw]
  );
  const principalSeries = useMemo(
    () => (principalChartType === 'top10' ? applyTop10PlusOthers(principalSeriesRaw) : principalSeriesRaw),
    [principalChartType, principalSeriesRaw]
  );

  const permiteDrilldownCidadeParaAssunto = dominantDimension === 'cidade' && secondaryDimension === 'assunto';

  const dadosParaGraficoSecundario = useMemo(() => {
    if (!permiteDrilldownCidadeParaAssunto || !cidadeSelecionadaNoGrafico) {
      return dadosBaseFiltrados;
    }

    return dadosBaseFiltrados.filter(
      (item) => normalizeDimensionValue(item, 'cidade') === cidadeSelecionadaNoGrafico
    );
  }, [dadosBaseFiltrados, permiteDrilldownCidadeParaAssunto, cidadeSelecionadaNoGrafico]);

  const secondarySeriesRaw = useMemo(
    () => buildSeriesByDimension(dadosParaGraficoSecundario, secondaryDimension),
    [dadosParaGraficoSecundario, secondaryDimension]
  );
  const secondaryChartType = useMemo(
    () => resolveChartTypeByCategoryCount(secondarySeriesRaw.length),
    [secondarySeriesRaw]
  );
  const secondarySeries = useMemo(
    () => (secondaryChartType === 'top10' ? applyTop10PlusOthers(secondarySeriesRaw) : secondarySeriesRaw),
    [secondaryChartType, secondarySeriesRaw]
  );

  const kpis = useMemo(() => ({
    totalOssComUso: getUniqueOssCountForItems(dadosBaseFiltrados),
    valorTotal: dadosBaseFiltrados.reduce((acc, item) => acc + Number(item.valor_total_revenda || 0), 0),
    quantidadeTotal: dadosBaseFiltrados.reduce((acc, item) => acc + Number(item.qtd_utilizada || 0), 0)
  }), [dadosBaseFiltrados]);

  React.useEffect(() => {
    setPage(1);
  }, [busca, filtroSubgrupo, filtroCidade, filtroAssunto]);

  React.useEffect(() => {
    if (!permiteDrilldownCidadeParaAssunto) {
      setCidadeSelecionadaNoGrafico(null);
      return;
    }

    if (cidadeSelecionadaNoGrafico) {
      const cidadeAindaExiste = principalSeriesRaw.some((item) => item.name === cidadeSelecionadaNoGrafico);
      if (!cidadeAindaExiste) {
        setCidadeSelecionadaNoGrafico(null);
      }
    }
  }, [permiteDrilldownCidadeParaAssunto, principalSeriesRaw, cidadeSelecionadaNoGrafico]);

  const totalPages = Math.ceil(dadosAgregadosTabela.length / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const dadosPaginados = dadosAgregadosTabela.slice(startIndex, startIndex + itemsPerPage);

  const getColumnStyle = (columnKey) => {
    if (columnKey === 'id_produto') return { minWidth: '80px', width: '5%' };
    if (columnKey === 'produto') return { minWidth: '200px', width: '20%' };
    if (columnKey === 'subgrupo') return { minWidth: '200px', width: '20%' };
    if (columnKey === 'cidade') return { minWidth: '160px', width: '15%' };
    if (columnKey === 'oss_com_uso') return { minWidth: '100px', width: '6%' };
    if (columnKey === 'qtd_utilizada') return { minWidth: '100px', width: '10%' };
    if (columnKey === 'valor_unitario_revenda') return { minWidth: '120px', width: '10%' };
    if (columnKey === 'valor_total_revenda') return { minWidth: '120px', width: '10%' };
    return { minWidth: '100px', width: '8%' };
  };

  const getColumnPadding = (columnKey) => {
    if (columnKey === 'id_produto' || columnKey.includes('qtd') || columnKey.includes('oss')) return 'px-2 py-1';
    if (columnKey.includes('valor') || columnKey === 'cidade') return 'px-2 py-2';
    return 'px-3 py-2';
  };

  const columns = [
    {
      key: 'id_produto',
      label: 'ID',
      sortable: true,
      className: 'text-left',
      render: (value) => (
        <span className="font-medium text-[color:var(--accent)] text-xs">
          {value?.toLocaleString('pt-BR') || value || 'N/A'}
        </span>
      )
    },
    {
      key: 'produto',
      label: 'Produto',
      sortable: true,
      className: 'text-left',
      cellClassName: 'px-3 py-2',
      render: (value) => (
        <span className="font-medium text-[color:var(--text-primary)] text-xs break-words" title={value}>
          {value || 'N/A'}
        </span>
      )
    },
    {
      key: 'subgrupo',
      label: 'Subgrupo',
      sortable: true,
      className: 'text-left',
      cellClassName: 'px-3 py-2',
      render: (value) => (
        <span className="text-xs text-[color:var(--text-muted)] break-words" title={value}>
          {value || 'N/A'}
        </span>
      )
    },
    {
      key: 'cidade',
      label: 'Cidade',
      sortable: true,
      className: 'text-left',
      render: (value) => (
        <span className="text-xs text-[color:var(--text-muted)]">
          {value || 'N/A'}
        </span>
      )
    },
    {
      key: 'oss_com_uso',
      label: 'OSS',
      sortable: true,
      className: 'text-right',
      render: (value) => (
        <span className="font-medium text-[color:var(--accent)] text-xs">
          {Number(value || 0).toLocaleString('pt-BR')}
        </span>
      )
    },
    {
      key: 'qtd_utilizada',
      label: 'Quantidade',
      sortable: true,
      className: 'text-right',
      render: (value) => (
        <span className="font-medium text-[color:var(--status-good)] text-xs">
          {Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
        </span>
      )
    },
    {
      key: 'valor_unitario_revenda',
      label: 'Valor Unitário',
      sortable: true,
      className: 'text-right',
      render: (value) => (
        <span className="font-medium text-[color:var(--text-primary)] text-xs">
          R$ {Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
        </span>
      )
    },
    {
      key: 'valor_total_revenda',
      label: 'Valor Total',
      sortable: true,
      className: 'text-right',
      render: (value) => (
        <span className="font-bold text-[color:var(--status-good)] text-xs">
          R$ {Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
        </span>
      )
    }
  ];

  const isKpiOnlyMode = dominantDimension === 'none';

  return (
    <div className="bg-[color:var(--surface)] rounded-lg shadow-sm border">
      <div className="px-6 py-4 border-b border-[color:var(--border)]">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <TrendingUp className="w-5 h-5 text-[color:var(--accent)] mr-2" />
            <h3 className="text-lg font-semibold text-[color:var(--text-primary)]">
              Análise de Produtos Movimentados OSS
            </h3>
          </div>
          <div className="text-sm text-[color:var(--text-muted)]">
            {dadosAgregadosTabela.length} {dadosAgregadosTabela.length === 1 ? 'item' : 'itens'}
          </div>
        </div>
      </div>

      <div className="px-6 py-4 border-b border-[color:var(--border)] space-y-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-[color:var(--text-muted)]" />
          </div>
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-[color:var(--border)] rounded-md leading-5 bg-[color:var(--surface)] text-[color:var(--text-primary)] placeholder-[color:var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)]"
            placeholder="Buscar por ID, produto ou subgrupo..."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-[color:var(--text-muted)] mb-1">
              Filtrar por Subgrupo
            </label>
            <MultiSelectFilter
              options={subgrupos}
              values={filtroSubgrupo}
              onChange={setFiltroSubgrupo}
              placeholder="Todos os subgrupos"
              label="Subgrupos"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[color:var(--text-muted)] mb-1 flex items-center gap-1">
              <MapPin className="h-4 w-4" />
              Filtrar por Cidade
            </label>
            <MultiSelectFilter
              options={cidades}
              values={filtroCidade}
              onChange={setFiltroCidade}
              placeholder="Todas as cidades"
              label="Cidades"
              icon={MapPin}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[color:var(--text-muted)] mb-1 flex items-center gap-1">
              <FileText className="h-4 w-4" />
              Filtrar por Assunto
            </label>
            <MultiSelectFilter
              options={assuntos}
              values={filtroAssunto}
              onChange={setFiltroAssunto}
              placeholder="Todos os assuntos"
              label="Assuntos"
              icon={FileText}
            />
          </div>
        </div>
      </div>

      <div className="px-6 pt-4">
        {dadosBaseFiltrados.length === 0 ? (
          <div className="bg-[color:var(--surface-muted)] rounded-lg border border-[color:var(--border)] p-6 text-sm text-[color:var(--text-muted)] text-center">
            Nenhum dado para exibir nos gráficos com os filtros atuais
          </div>
        ) : isKpiOnlyMode ? (
          <div className="bg-[color:var(--surface-muted)] rounded-lg border border-[color:var(--border)] p-4">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="h-4 w-4 text-[color:var(--text-muted)]" />
              <h4 className="text-sm font-semibold text-[color:var(--text-primary)]">Modo KPI (sem comparação)</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="rounded-md border border-[color:var(--border)] bg-[color:var(--surface)] p-4 text-center">
                <p className="text-xs text-[color:var(--text-muted)]">Total OSS com Uso</p>
                <p className="text-lg font-semibold text-[color:var(--text-primary)]">
                  {kpis.totalOssComUso.toLocaleString('pt-BR')}
                </p>
              </div>
              <div className="rounded-md border border-[color:var(--border)] bg-[color:var(--surface)] p-4 text-center">
                <p className="text-xs text-[color:var(--text-muted)]">Valor Total</p>
                <p className="text-lg font-semibold text-[color:var(--text-primary)]">
                  R$ {kpis.valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div className="rounded-md border border-[color:var(--border)] bg-[color:var(--surface)] p-4 text-center">
                <p className="text-xs text-[color:var(--text-muted)]">Quantidade Total</p>
                <p className="text-lg font-semibold text-[color:var(--text-primary)]">
                  {kpis.quantidadeTotal.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className={`grid grid-cols-1 ${secondaryDimension ? 'xl:grid-cols-2' : ''} gap-4`}>
            <DynamicBarChartCard
              title={`Gráfico Principal por ${DIMENSION_LABELS[dominantDimension]}`}
              subtitle={principalChartType === 'top10' ? 'Top 10 + Outros (OSS com uso)' : 'OSS com uso'}
              series={principalSeries}
              chartType={principalChartType}
              barColor={BAR_COLOR_PRIMARY}
              onBarClick={permiteDrilldownCidadeParaAssunto ? (entry) => {
                const cidade = entry?.name;
                if (!cidade) return;
                setCidadeSelecionadaNoGrafico((prev) => (prev === cidade ? null : cidade));
              } : null}
              activeCategory={permiteDrilldownCidadeParaAssunto ? cidadeSelecionadaNoGrafico : null}
            />
            {secondaryDimension && (
              <DynamicBarChartCard
                title={`Gráfico Secundário por ${DIMENSION_LABELS[secondaryDimension]}`}
                subtitle={
                  `${secondaryChartType === 'top10' ? 'Top 10 + Outros (OSS com uso)' : 'OSS com uso'}${
                    permiteDrilldownCidadeParaAssunto && cidadeSelecionadaNoGrafico
                      ? ` | Cidade: ${cidadeSelecionadaNoGrafico}`
                      : ''
                  }`
                }
                series={secondarySeries}
                chartType={secondaryChartType}
                barColor={BAR_COLOR_SECONDARY}
              />
            )}
          </div>
        )}
      </div>

      <div className="px-6 py-4">
        <DataTable
          data={dadosPaginados}
          columns={columns}
          loading={loading}
          maxHeight="max-h-96"
          emptyMessage="Nenhuma análise encontrada"
          customColumnWidths={getColumnStyle}
          customColumnPadding={getColumnPadding}
          customFontSize="text-xs"
        />
      </div>

      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-[color:var(--border)]">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={setItemsPerPage}
            totalItems={dadosAgregadosTabela.length}
          />
        </div>
      )}

      {dadosAgregadosTabela.length > 0 && (
        <div className="px-6 py-4 bg-[color:var(--surface-muted)] border-t border-[color:var(--border)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="text-center">
              <p className="text-sm font-medium text-[color:var(--text-muted)]">Total OSS com Uso</p>
              <p className="text-lg font-semibold text-[color:var(--accent)]">
                {kpis.totalOssComUso.toLocaleString('pt-BR')}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-[color:var(--text-muted)]">Valor Total</p>
              <p className="text-lg font-semibold text-[color:var(--status-good)]">
                R$ {kpis.valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnaliseProdutosMovimentadosTable;
